import router from '@/router/router';
import store from '@/store';
import AccountApiProxyService from '@/services/AccountApiProxyService';
import SettingService from '@/services/AccountApiSettingsService';
import {
  i18n,
  loadLanguageAsync,
  loadLanguageBasedOnBrowserPreference,
} from '@/i18n';
import AccountStaticService from '@/services/AccountStaticService';
import { AccountConstants } from '@/constants';
import {
  getStaticAssetsBaseUrl,
  replaceFordProUrlDomainBasedOnCountry,
  getFordProRedirectPath,
  getFordProDomain,
  buildFordProLoginRedirect,
} from '@/urlUtils';
import fpgConfiguration from '@/store/fpgConfiguration';
import { removeLeadingSlash, removeTrailingSlash } from '@/utils';

const authService = {
  performAuthenticationLoginAction(action) {
    return store
      .set('configuration/fetch!', 'loginUrl')
      .then((loginUrl) => action(loginUrl));
  },
  performAuthenticationLogoutAction(action) {
    return store
      .set('configuration/fetch!', 'logoutUrl')
      .then((logoutUrl) => action(logoutUrl));
  },
  goToLogin(returnPage) {
    if (!this.isAuthenticated()) {
      localStorage.setItem('dmShowVehicleOwnershipConfirmationDialog', 'true');
      return this.performAuthenticationLoginAction((loginUrl) => {
        window.location.assign(loginUrl.replace('{redirect}', '/'));
        let redirect = '/';
        let modifiedReturnPage = returnPage;
        if (fpgConfiguration.state.isTileMode) {
          modifiedReturnPage = `${removeTrailingSlash(
            window.location.origin
          )}/${removeLeadingSlash(returnPage)}`;
        }
        if (returnPage) {
          if (returnPage.length > 1) {
            localStorage.setItem('firstEvent', false);
            redirect = modifiedReturnPage;
          }
        }
        window.location.assign(loginUrl.replace('{redirect}', redirect));
      });
    } else {
      return router.push('/');
    }
  },

  goToLogout() {
    localStorage.clear();
    sessionStorage.clear();
    return this.performAuthenticationLogoutAction((logoutUrl) => {
      window.location.assign(logoutUrl);
    });
  },

  getUserInfo() {
    return AccountApiProxyService.get(
      AccountConstants.ACCOUNT_API_PATH + '/authorize/userinfo',
      {
        withCredentials: true,
      }
    );
  },
  getPartnerTokenToRedirect(applicationId, companyCountryCode, state) {
    const stateParam = state
      ? `&state=${encodeURIComponent(btoa(JSON.stringify(state)))}`
      : '';
    return AccountApiProxyService.get(
      '/pro/auth/v1/partner-app?redirect=false&app-id=' +
        applicationId +
        stateParam,
      { withCredentials: true }
    ).then((res) => {
      if (!this.isExcludedFromDomainRewrite(applicationId)) {
        res.data = replaceFordProUrlDomainBasedOnCountry(
          res.data,
          companyCountryCode
        );
      }
      return res;
    });
  },

  isExcludedFromDomainRewrite(applicationId) {
    const excludedApplicationIds =
      store.state.configuration.fordProDomainRewriteExcludedApplicationIds ||
      [];
    return excludedApplicationIds.some(
      (excludedId) =>
        typeof excludedId === 'string' &&
        typeof applicationId === 'string' &&
        excludedId.toLowerCase() === applicationId.toLowerCase()
    );
  },

  async authenticateUser(currentPath = null) {
    function loadLanguage(userLanguage) {
      loadLanguageAsync(userLanguage).catch((err) => {
        console.log(err);
      });
    }

    try {
      let response = await this.getUserInfo();
      if (response.status === 200) {
        store.dispatch('auth/setUserInfo', response.data);

        const willAutoRedirectToFordPro =
          typeof currentPath === 'string' && this.shouldAutoRedirectToFordPro();
        if (willAutoRedirectToFordPro) {
          store.set('fpgConfiguration/isAutoRedirectingToFordPro', true);
        }

        let userLanguage;
        try {
          const languageSettingResponse = await SettingService.getUserLanguage(
            response.data.userId
          );
          if (languageSettingResponse.status === 200) {
            userLanguage = languageSettingResponse.data;
            await store.dispatch('auth/setLanguage', userLanguage);
          } else {
            userLanguage = store.state.auth['language'];
          }
        } catch (err) {
          console.log(
            'Unable to fetch user language settings, falling back to existing language. Error: ',
            err
          );
          userLanguage = store.state.auth['language'];
        }
        loadLanguage(userLanguage);
        let languageCheck = '/getstarted/' + userLanguage + '.pdf';
        let pdfUrl =
          getStaticAssetsBaseUrl() +
          '/getstarted/' +
          AccountConstants.DEFAULT_LANGUAGE +
          '.pdf';

        await AccountStaticService.get(languageCheck)
          .then(() => {
            pdfUrl =
              getStaticAssetsBaseUrl() + '/getstarted/' + userLanguage + '.pdf';
          })
          .catch((err) => {
            console.log(
              'Unable to fetch pdf for language ' +
                userLanguage +
                ' fetching default pdf. Error: ',
              err
            );
          });

        store.dispatch('auth/setIsLoggedIn', true);
        store.dispatch('auth/setGetStartedPdfUrl', pdfUrl);

        // Auto-redirect for decommissioned countries
        if (willAutoRedirectToFordPro) {
          const companyCountryCode =
            store.getters['auth/getCompanyCountryCode'];
          const fordProPath = getFordProRedirectPath(currentPath);
          const redirectState = {
            redirect: '/' + (userLanguage || i18n.locale) + fordProPath,
          };
          try {
            const res = await this.getPartnerTokenToRedirect(
              store.state.configuration.fpgApplicationId,
              companyCountryCode,
              redirectState
            );
            window.location.replace(
              buildFordProLoginRedirect(res.data, redirectState.redirect)
            );
          } catch (error) {
            console.error(error);
            window.location.replace(getFordProDomain(companyCountryCode));
          }
          return { redirected: true };
        }

        if (
          response.data.authorities.indexOf('TERMS_AND_CONDITIONS_SIGN') !==
            -1 &&
          !store.getters['termsAndConditions/hasFetchedTermsAndConditions']
        ) {
          const TCResponse = await this.getHasSignedTermsAndConditions();
          await store.dispatch(
            'termsAndConditions/setSignedTermsAndConditions',
            TCResponse.data
          );
          await store.dispatch(
            'termsAndConditions/setFetchedTermsAndConditions',
            true
          );
          await store.dispatch(
            'termsAndConditions/setCanViewOnboardPage',
            true
          );
          await router.push('/onboard');
        }
      } else {
        loadLanguageBasedOnBrowserPreference().catch((err) => console.log(err));
        console.log('User info not found:');
        console.log(response);
        store.dispatch('auth/setIsLoggedIn', false);
      }
    } catch (error) {
      loadLanguageBasedOnBrowserPreference().catch((err) => console.log(err));
      console.log('Error retrieving user info:');
      console.log(JSON.stringify(error));
      store.dispatch('auth/setIsLoggedIn', false);
    }
  },

  shouldAutoRedirectToFordPro() {
    return store.getters['fpgConfiguration/shouldAutoRedirectToFordPro'];
  },

  isAuthenticated() {
    return store.state.auth['isLoggedIn'];
  },

  async getHasSignedTermsAndConditions() {
    return AccountApiProxyService.get(
      AccountConstants.ACCOUNT_API_PATH + '/v1/policy/termsAndConditions/agreed'
    );
  },
};

export default authService;
